import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import MainLayout from "../../Layouts/MainLayout";
import { useSelector } from "react-redux";
import ChartJSCard from "../../Dashboard/ChartJSCard";
import DashboardCard from "../../Dashboard/DashboardCard";
import { FaUserCircle } from "react-icons/fa";
import { FiUser, FiMail, FiShield, FiTrendingUp, FiActivity, FiStar, FiClock, FiTarget, FiAward, FiBarChart2 } from "react-icons/fi";
import Breadcrumb from "../../Breadcrumb";
import apiClient from '../../../api/apiClient';

function UsersPerformance() {
  const { id } = useParams(); // Extract the user ID from the URL
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
  );

  useEffect(() => {
    const handleDarkModeChange = (e) => {
      setIsDarkMode(e.matches);
    };

    window
      .matchMedia("(prefers-color-scheme: dark)")
      .addEventListener("change", handleDarkModeChange);

    return () => {
      window
        .matchMedia("(prefers-color-scheme: dark)")
        .removeEventListener("change", handleDarkModeChange);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `/api/dashboard/performance-overview/`,
          {
            params: { id }, // Pass the user ID as a query parameter
          }
        );
        setData(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(
          error.response?.data?.error || "Failed to load user performance data."
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    } else {
      setError("No user ID provided in the URL.");
      setLoading(false);
    }
  }, [id]);

  // Create ChartJS data for performance line chart
  const lineChartData = useMemo(() => {
    // Get all unique dates from both attempted and reviewed tasks
    const attemptedDates = data.daily_performance?.attempted?.map(item => item.created_at__date) || [];
    const reviewedDates = data.daily_performance?.reviewed?.map(item => item.reviewed_at__date) || [];
    const allDates = [...new Set([...attemptedDates, ...reviewedDates])].sort();

    return {
      labels: allDates,
      datasets: [
        {
          label: 'Attempted Tasks',
          data: allDates.map(date => {
            const attemptedItem = data.daily_performance?.attempted?.find(
              item => item.created_at__date === date
            );
            return attemptedItem ? attemptedItem.count : 0;
          }),
          backgroundColor: 'rgba(99, 102, 241, 0.4)',
          borderColor: 'rgba(79, 70, 229, 1)',
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 6,
          pointBackgroundColor: 'rgba(79, 70, 229, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          fill: true,
        },
        {
          label: 'Reviewed Tasks',
          data: allDates.map(date => {
            const reviewedItem = data.daily_performance?.reviewed?.find(
              item => item.reviewed_at__date === date
            );
            return reviewedItem ? reviewedItem.count : 0;
          }),
          backgroundColor: 'rgba(236, 72, 153, 0.4)',
          borderColor: 'rgba(219, 39, 119, 1)',
          borderWidth: 3,
          tension: 0.4,
          pointRadius: 6,
          pointBackgroundColor: 'rgba(219, 39, 119, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          fill: true,
        },
      ],
    };
  }, [data.daily_performance]);

  // Create ChartJS data for overall performance donut chart
  const donutChartData = useMemo(() => ({
    labels: ['Total Attempted', 'Accepted', 'Rejected'],
    datasets: [
      {
        data: [
          data.overall_performance?.total_tasks_attempted || 0,
          data.overall_performance?.total_tasks_accepted || 0,
          data.overall_performance?.total_tasks_rejected || 0,
        ],
        backgroundColor: [
          'rgba(79, 70, 229, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(239, 68, 68, 0.8)',
        ],
        borderColor: isDarkMode ? '#1F2937' : '#FFFFFF',
        borderWidth: 3,
        hoverOffset: 10,
      },
    ],
  }), [data.overall_performance, isDarkMode]);

  const getUserInitials = (firstName, lastName, email) => {
    // If we have first and last name, use them
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    // If we only have first name, use first two letters
    if (firstName && firstName.length >= 2) {
      return firstName.substring(0, 2).toUpperCase();
    }
    // If we only have last name, use first two letters
    if (lastName && lastName.length >= 2) {
      return lastName.substring(0, 2).toUpperCase();
    }
    // Fallback to email initials
    if (email) {
      const emailName = email.split('@')[0];
      if (emailName.length >= 2) {
        return emailName.substring(0, 2).toUpperCase();
      }
      return emailName.charAt(0).toUpperCase() + 'U';
    }
    // Ultimate fallback
    return 'U';
  };

  const getUserDisplayName = (firstName, lastName, email) => {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }
    // Extract name from email
    if (email) {
      const emailName = email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return 'Unknown User';
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      "admin": "bg-gradient-to-r from-green-500 to-teal-500 text-white",
      "reviewer": "bg-gradient-to-r from-blue-500 to-cyan-500 text-white",
      "tasker": "bg-gradient-to-r from-orange-500 to-amber-500 text-white"
    };
    return colors[role?.toLowerCase()] || "bg-gray-500 text-white";
  };

  const calculateSuccessRate = () => {
    const accepted = data.overall_performance?.total_tasks_accepted || 0;
    const attempted = data.overall_performance?.total_tasks_attempted || 0;
    const rejected = data.overall_performance?.total_tasks_rejected || 0;
    
    // Handle different user roles correctly:
    // - For taskers: success rate is accepted tasks divided by their attempted tasks
    // - For reviewers: success rate is accepted tasks divided by total reviewed tasks (accepted + rejected)
    let rate = 0;
    
    if (data.user_data?.role?.toLowerCase() === 'reviewer') {
      // For reviewers, calculate based on their review decisions
      const totalReviewed = accepted + rejected;
      rate = totalReviewed > 0 ? (accepted / totalReviewed) * 100 : 0;
    } else {
      // For taskers, calculate based on their submitted tasks
      rate = attempted > 0 ? (accepted / attempted) * 100 : 0;
    }
    
    // Ensure the rate is never more than 100%
    return Math.min(100, Math.round(rate));
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="p-4 md:ml-64 pt-20">
          {/* Header Section */}
          <div className="max-w-7xl mx-auto mb-8">
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-3xl shadow-sm border border-white/20 dark:border-gray-700/30 p-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-r from-green-500 to-teal-500 rounded-2xl shadow-lg">
                    <FiTrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                      User Performance
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1 text-lg">
                      Detailed analytics and insights
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Breadcrumb */}
              <div className="mt-6 pt-6 border-t border-gray-200/50 dark:border-gray-600/50">
                <Breadcrumb
                  items={[
                    { label: "Users", path: "/users" },
                    { label: "User Performance" },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="max-w-7xl mx-auto space-y-8">
            {loading ? (
              <div className="space-y-6">
                {/* Loading skeleton */}
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-8 animate-pulse">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-2xl"></div>
                    <div className="space-y-2">
                      <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-48"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-32"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="h-32 bg-gray-200 dark:bg-gray-600 rounded-xl"></div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-8 animate-pulse">
                      <div className="h-64 bg-gray-200 dark:bg-gray-600 rounded-xl"></div>
                    </div>
                  ))}
                </div>
              </div>
            ) : error ? (
              <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-12 text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FiUser className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Error Loading Performance Data
                </h3>
                <p className="text-red-600 dark:text-red-400">{error}</p>
              </div>
            ) : (
              <>
                {/* User Information Header */}
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 overflow-hidden">
                  <div className="h-24 bg-gradient-to-r from-green-500 to-teal-500 relative">
                    <div className="absolute inset-0 bg-black/10"></div>
                    <div className="absolute -bottom-8 left-8">
                      <div className="w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 flex items-center justify-center shadow-lg border-4 border-white dark:border-gray-800">
                        <span className="text-xl font-bold text-gray-700 dark:text-gray-300">
                          {getUserInitials(data.user_data?.first_name, data.user_data?.last_name, data.user_data?.email)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="pt-12 p-8">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                          {getUserDisplayName(data.user_data?.first_name, data.user_data?.last_name, data.user_data?.email)}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 flex items-center gap-2 mb-4">
                          <FiMail className="w-4 h-4" />
                          {data.user_data?.email}
                        </p>
                        <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${getRoleBadgeColor(data.user_data?.role)}`}>
                          <FiShield className="inline w-4 h-4 mr-2" />
                          {data.user_data?.role?.charAt(0).toUpperCase() + data.user_data?.role?.slice(1)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-sm rounded-xl p-4 border border-green-200/30 dark:border-green-700/30">
                          <div className="flex items-center justify-between mb-2">
                            <FiTarget className="w-5 h-5 text-green-600 dark:text-green-400" />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Success Rate</span>
                          </div>
                          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{calculateSuccessRate()}%</p>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50/50 to-cyan-50/50 dark:from-blue-900/20 dark:to-cyan-900/20 backdrop-blur-sm rounded-xl p-4 border border-blue-200/30 dark:border-blue-700/30">
                          <div className="flex items-center justify-between mb-2">
                            <FiActivity className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Total Attempts</span>
                          </div>
                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{data.total_tasks_attempted || 0}</p>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20 backdrop-blur-sm rounded-xl p-4 border border-purple-200/30 dark:border-purple-700/30">
                          <div className="flex items-center justify-between mb-2">
                            <FiStar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Total Reviews</span>
                          </div>
                          <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{data.total_tasks_reviewed || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Performance Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 overflow-hidden">
                    <div className="p-6 border-b border-gray-200/50 dark:border-gray-600/50">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <FiAward className="w-5 h-5 text-green-500" />
                        Overall Performance
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                        Task completion breakdown
                      </p>
                    </div>
                    <div className="p-6">
                      {data.overall_performance ? (
                        <ChartJSCard 
                          type="doughnut"
                          data={donutChartData}
                          isDarkMode={isDarkMode}
                        />
                      ) : (
                        <div className="h-60 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                          <FiBarChart2 className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" />
                          <p className="text-lg font-medium">No performance data available</p>
                          <p className="text-sm">Data will appear once user starts completing tasks</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <FiActivity className="w-5 h-5 text-green-500" />
                        Today's Activity
                      </h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-900/20 dark:to-emerald-900/20 backdrop-blur-sm rounded-xl p-4 border border-green-200/30 dark:border-green-700/30">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Attempted Today</p>
                          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{data.total_tasks_attempted_today || 0}</p>
                        </div>
                        <div className="bg-gradient-to-r from-blue-50/50 to-cyan-50/50 dark:from-blue-900/20 dark:to-cyan-900/20 backdrop-blur-sm rounded-xl p-4 border border-blue-200/30 dark:border-blue-700/30">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Reviewed Today</p>
                          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{data.total_tasks_reviewed_today || 0}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 p-6">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                        <FiTarget className="w-5 h-5 text-green-500" />
                        Performance Metrics
                      </h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Acceptance Rate</span>
                          <span className="text-lg font-bold text-green-600 dark:text-green-400">{calculateSuccessRate()}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-teal-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${calculateSuccessRate()}%` }}
                          ></div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center pt-2">
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Accepted</p>
                            <p className="font-bold text-green-600 dark:text-green-400">{data.overall_performance?.total_tasks_accepted || 0}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Rejected</p>
                            <p className="font-bold text-red-600 dark:text-red-400">{data.overall_performance?.total_tasks_rejected || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Daily Performance Chart */}
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl shadow-sm border border-white/20 dark:border-gray-700/30 overflow-hidden">
                  <div className="p-6 border-b border-gray-200/50 dark:border-gray-600/50">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                      <FiBarChart2 className="w-5 h-5 text-green-500" />
                      Daily Performance Trends
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                      Track daily activity over time
                    </p>
                  </div>
                  <div className="p-6">
                    {(data.daily_performance?.attempted?.length > 0 || data.daily_performance?.reviewed?.length > 0) ? (
                      <ChartJSCard 
                        type="bar"
                        data={lineChartData}
                        isDarkMode={isDarkMode}
                      />
                    ) : (
                      <div className="h-60 flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                        <FiBarChart2 className="w-12 h-12 mb-4 text-gray-300 dark:text-gray-600" />
                        <p className="text-lg font-medium">No daily performance data available</p>
                        <p className="text-sm">Historical data will appear as user activity increases</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default UsersPerformance;